import React, { useEffect, useState } from 'react';
import { auth, db } from './lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';

// The Firebase Function URL for creating a Stripe Checkout session
const FUNCTIONS_BASE = 'https://us-east1-slatevault.cloudfunctions.net';

export default function SlateVaultGate({ children }) {
  const [user, setUser]     = useState(undefined); // undefined = loading
  const [sub, setSub]       = useState(undefined); // undefined = loading
  const [loading, setLoading] = useState(false);

  // Watch auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) setSub(null);
    });
    return unsub;
  }, []);

  // Watch subscription doc in Firestore
  useEffect(() => {
    if (!user) return;
    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      setSub(snap.exists() ? snap.data() : null);
    });
    return unsub;
  }, [user]);

  const handleSubscribe = async () => {
    if (!user) {
      window.location.hash = '#/slatevault/auth';
      return;
    }
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/createCheckoutSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          successUrl: `${window.location.origin}${window.location.pathname}#/slatevault/app?subscribed=true`,
          cancelUrl:  `${window.location.origin}${window.location.pathname}#/slatevault`,
        }),
      });
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
      setLoading(false);
    }
  };

  const handleManage = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/createPortalSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          returnUrl: `${window.location.origin}${window.location.pathname}#/slatevault/app`,
        }),
      });
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      console.error('Portal error:', err);
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    auth.signOut();
    window.location.hash = '#/slatevault';
  };

  // Still loading auth or subscription
  if (user === undefined || (user && sub === undefined)) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div className="w-5 h-5 border-2 border-zinc-700 border-t-sky-400 rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <Paywall
        title="Sign In Required"
        body="Create a free account to start your SlateVault subscription."
        cta="CREATE ACCOUNT"
        onCta={() => { window.location.hash = '#/slatevault/auth'; }}
        secondaryCta="Already have an account? Sign in"
        onSecondaryCta={() => { window.location.hash = '#/slatevault/auth'; }}
        loading={loading}
      />
    );
  }

  // Logged in but no active subscription
  const isActive = sub?.subscriptionStatus === 'active' || sub?.subscriptionStatus === 'trialing';
  if (!isActive) {
    return (
      <Paywall
        title="Subscribe to Access"
        body={`You're signed in as ${user.email}. Subscribe to unlock SlateVault for $49/month.`}
        cta={loading ? 'REDIRECTING TO STRIPE…' : 'SUBSCRIBE — $49/MONTH'}
        onCta={handleSubscribe}
        secondaryCta="Sign out"
        onSecondaryCta={handleSignOut}
        loading={loading}
      />
    );
  }

  // Active subscriber — render the portal with a minimal top bar
  return (
    <div>
      <div className="border-b border-zinc-900 px-6 py-3 flex items-center justify-between bg-[#050505]" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div className="flex items-center gap-4">
          <a href="/#/tools" style={{ textDecoration: 'none' }} className="text-[11px] tracking-[0.2em] text-zinc-500 hover:text-sky-400 uppercase font-bold transition-colors">
            ← Tools
          </a>
          <span className="text-zinc-800">|</span>
          <span className="text-[11px] tracking-widest text-white font-black uppercase">SlateVault</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[10px] text-zinc-600 tracking-wider">{user.email}</span>
          <button onClick={handleManage} disabled={loading} className="text-[10px] tracking-widest text-zinc-500 hover:text-sky-400 uppercase font-bold transition-colors disabled:opacity-50">
            Manage Plan
          </button>
          <button onClick={handleSignOut} className="text-[10px] tracking-widest text-zinc-600 hover:text-zinc-400 uppercase transition-colors">
            Sign Out
          </button>
        </div>
      </div>
      {children}
    </div>
  );
}

function Paywall({ title, body, cta, onCta, secondaryCta, onSecondaryCta, loading }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="w-full max-w-md text-center">
        <a href="/#/" style={{ textDecoration: 'none' }}>
          <img src="/logo.png" alt="Aperture Slate" className="h-10 mx-auto mb-8 opacity-60" />
        </a>
        <p className="text-[10px] tracking-[0.4em] text-sky-400 font-bold uppercase mb-6">SlateVault</p>
        <h2 className="text-3xl font-black tracking-tight uppercase mb-4">{title}</h2>
        <p className="text-zinc-500 text-sm leading-relaxed mb-8 max-w-xs mx-auto">{body}</p>
        <button
          onClick={onCta}
          disabled={loading}
          className="w-full bg-white text-black font-black text-[11px] tracking-widest uppercase py-4 hover:bg-sky-400 transition-colors disabled:opacity-50 mb-4"
        >
          {cta}
        </button>
        {secondaryCta && (
          <button onClick={onSecondaryCta} className="text-[10px] text-zinc-600 hover:text-zinc-400 tracking-widest uppercase transition-colors">
            {secondaryCta}
          </button>
        )}
        <div className="mt-8">
          <a href="/#/slatevault" style={{ textDecoration: 'none' }} className="text-[10px] text-zinc-700 hover:text-zinc-500 tracking-widest uppercase transition-colors">
            ← Back to SlateVault
          </a>
        </div>
      </div>
    </div>
  );
}
